import { createAsyncThunk } from '@reduxjs/toolkit'
import _ from 'lodash'
import { Geometry } from 'ol/geom'
import { Style } from 'ol/style'
import placeServiceAPI from '../../apis/PlaceServiceAPI'
import { forceManualRecaptcha, RecaptchaAction, RecaptchaPayload } from '../../common/slice'
import { RootState } from '../../common/store'
import { PayloadAction } from '../../common/types'
import RecaptchaRequiredError from '../../errors/RecaptchaRequiredError'
import { LocationData } from '../map/MapComponent'

// Action Types
export const UPDATE_SEARCH_FILTERS = 'UPDATE_SEARCH_FILTERS'
export const RESET_SEARCH_FILTERS = 'RESET_SEARCH_FILTERS'
export const UPDATE_PLACENAME_DATA = 'UPDATE_PLACENAME_DATA'
export const RESET_PLACENAME_DATA = 'RESET_PLACENAME_DATA'
export const SELECT_PLACENAME_PARCEL = 'SELECT_PLACENAME_PARCEL'
export const GET_CANDIDATE_PLACES = 'GET_CANDIDATE_PLACES'
export const UPDATE_CANDIDATE_PLACES_DATA = 'UPDATE_CANDIDATE_PLACES_DATA'
export const UPDATE_SEARCH_VALUE = 'UPDATE_SEARCH_VALUE'
export const RESET_SEARCH_VALUE = 'RESET_SEARCH_VALUE'
export const RESET_PLACENAME_MAP_VIEW = 'RESET_PLACENAME_MAP_VIEW'


// Selectors
export const getSearchFilters = (state: RootState) => {
  return state.searchFilters as SearchFilters
}

export const getCandidatePlacesBase = (state: RootState) => {
  return state.candidatePlaces
}
export const getPlaces = (state: RootState) => {
  return getCandidatePlacesBase(state).parcels
}
export const arePlacesLoading = (state: RootState) => {
  return getCandidatePlacesBase(state).isLoading
}
export const getPlacenameData = (state: RootState) => {
  return state.placenameData
}

export const getPlacenameSearchValue = (state: RootState) => {
  return state.searchValue
}

export const hasResetPlaceMapView = (state: RootState) => {
  return state.resetPlaceMapView
}


// Actions
export const updateSearchFilters = (searchFilters: SearchFilters): UpdateSearchFiltersAction => {
  return {
    type: UPDATE_SEARCH_FILTERS,
    payload: searchFilters,
  }
}

export const resetSearchFilters = () => {
  return {
    type: RESET_SEARCH_FILTERS,
  }
}

export const updatePlacenameData = (placenameData: PlacenameDataType) => {
  return {
    type: UPDATE_PLACENAME_DATA,
    payload: placenameData,
  }
}

export const resetPlacenameData = () => {
  return {
    type: RESET_PLACENAME_DATA,
  }
}

export const resetPlaceMapView = (resetPlaceMapView?: boolean) => {
  return {
    type: RESET_PLACENAME_MAP_VIEW,
    payload: resetPlaceMapView,
  }
}

export const selectPlacenameParcel = createAsyncThunk(
  SELECT_PLACENAME_PARCEL,
  async (selectedPlaceParcel: string, thunkAPI) => {
    try {
      const payload = await placeServiceAPI.fetchPlacename(selectedPlaceParcel)
      if (payload) {
        payload.lastUpdateTime = new Date().getTime()
        thunkAPI.dispatch(updatePlacenameData(payload))
      }
    } catch (e) {
    }
  })

export const getCandidatePlaces = createAsyncThunk(
  GET_CANDIDATE_PLACES,
  async (searchOptionsData: SearchOptionsData, thunkAPI) => {
    try {
      const payload = await placeServiceAPI.fetchCandidatePlaces(searchOptionsData)
      if (payload) {
        thunkAPI.dispatch(updateCandidatePlacesData(payload))
      }
    } catch (e) {
      if (e instanceof RecaptchaRequiredError) {
        const action: RecaptchaAction<SearchOptionsData> = {
          type: GET_CANDIDATE_PLACES,
          payload: searchOptionsData,
        }
        thunkAPI.dispatch(forceManualRecaptcha(action))
      } else {
        throw e
      }
    }
  })

export const updatePlacenameSearchValue = (searchValue?: string) => {
  return {
    type: UPDATE_SEARCH_VALUE,
    payload: searchValue,
  }
}

export const updateCandidatePlacesData = (results: PlacenameDataType[]) => ({
  type: UPDATE_CANDIDATE_PLACES_DATA,
  payload: results,
})


// Types

export interface PlacenameDataType extends LocationData {
  title: string
  description?: string
  localities?: string
  lastUpdateTime: number
  searchTerm: string
  id: number
  placeId: string
  error: boolean
  errorType: string
  localGovernmentAreas?: string
  officialName?: string
  typeDesignation?: string
  alternateNames?: string
  dataType?: string
  placeStatus?: string
  dateRegistered?: string
  historyOrigin?: string
  gazettal?: GazettalData[]
  namingThemes?: string
  altGeometry?: GeoLatte[]
  phoneticSpelling?: string
}

export interface SearchOptionsData extends RecaptchaPayload {
  searchFilters?: SearchFilters | string
  phoneticSearch?: boolean
}

export interface SearchFilters extends SearchOptionsData {
  councils?: string[]
  localities?: string[]
  placeTypes?: string[]
  startDate?: string
  endDate?: string
  namingThemes?: string[]
  typeDesignations?: string[]
  status?: string[]
  restrictive?: boolean
  searchTerm?: string
  fileTypes?: string[]
}

export type UpdateSearchFiltersAction = {
  type: string
  payload: SearchFilters
}

export type PlacenameDataTypeProp = {
  placenameData: PlacenameDataType
}

export type GazettalData = {
  id: string
  date: Date
  gazettal: string
  comment?: string
}

export interface GeoLatte extends Geometry {
  type: string
}

type CandidatePlacesBase = {
  parcels: PlacenameDataType[]
  isLoading: boolean
}

export interface PlacenameSearchOptionData {
  code: string
  value: string
}

export type dropdownOptions = {
  id: number
  text: string
  value: string
}

export interface PlacenameDocument {
  mediaId: number
  placeId: number
  description: string | null
  fileType: string
  mimeType: string
  category: string
  friendlyCategory: string
  mediaUrl: string
}

export type StyledLayerType = {
  fillColor: string,
  type: string | undefined,
  style: Style,
  className: string,
  data: PlacenameDataType[]
}

//Reducers
export const searchFiltersReducer = (searchFilters: SearchFilters = {}, {
  type,
  payload,
}: UpdateSearchFiltersAction): SearchFilters => {

  if (type === UPDATE_SEARCH_FILTERS) {
    return payload
  }

  if (type === RESET_SEARCH_FILTERS) {
    return {}
  }

  return searchFilters
}

export const updatePlacenameDataReducer = (placenameData: PlacenameDataType | null = null, action: PayloadAction<PlacenameDataType>) => {

  if (action.type === UPDATE_PLACENAME_DATA) {
    return action.payload
  }

  if (action.type === RESET_PLACENAME_DATA) {
    return null
  }

  return placenameData
}

export const candidatePlacesReducer = (state: CandidatePlacesBase = { isLoading: false, parcels: [] }, {
  type,
  payload,
}: { type: string, payload: PlacenameDataType[] }) => {
  if (type === UPDATE_CANDIDATE_PLACES_DATA) {
    let parcels = payload
    if (_.isEmpty(parcels)) {
      parcels = []
    }
    state = { ...state, parcels, isLoading: false }
  } else if (type === GET_CANDIDATE_PLACES) {
    state = { ...state, isLoading: true }
  } else if (type === `${GET_CANDIDATE_PLACES}/pending`) {
    state = { ...state, isLoading: true }
  }
  return state
}

export const searchValueReducer = (searchValue = '', { type, payload }: { type: string, payload: string }) => {

  if (type === UPDATE_SEARCH_VALUE) {
    return payload
  }

  if (type === RESET_SEARCH_VALUE) {
    return ''
  }

  return searchValue
}

export const resetPlaceViewReducer = (resetPlaceMapView = false, { type, payload }: {
  type: string,
  payload: string
}) => {

  if (type === RESET_PLACENAME_MAP_VIEW) {
    return payload
  }

  return resetPlaceMapView
}

